import z from 'zod';
import {
  AllCharLabelSchema,
  CognitoUserIdSchema,
  UUIDSchema,
  UnixTimestampSchema,
} from '../common/schemas';
import {
  DBJsonObjectSchema,
  DBSha256Schema,
  DBTimestampSchema,
  JsonObjectSchema,
  Sha256Schema,
} from '../common/sql';
import { ProcessingStatus } from '../sourceDocuments/enums';

/**
 * DB Schemas
 */

export const DBEnrichmentRunIdSchema = UUIDSchema;
export const DBEnrichmentRunEngineNameSchema = z.string().min(1).max(128);
export const DBEnrichmentRunEngineVersionSchema = z.string().min(1).max(64);
export const DBEnrichmentRunModelSchema = z.string().min(1).max(128).nullable();
/** Every status but awaiting_review, which is a document's alone. */
export const DBEnrichmentRunStatusSchema = z.enum(ProcessingStatus).exclude(['AwaitingReview']);
export const DBEnrichmentRunStatusDetailSchema = AllCharLabelSchema.min(1).max(2048).nullable();

export const DBEnrichmentRunSchema = z.object({
  id: DBEnrichmentRunIdSchema,
  questionId: UUIDSchema,
  engineName: DBEnrichmentRunEngineNameSchema,
  engineVersion: DBEnrichmentRunEngineVersionSchema,
  model: DBEnrichmentRunModelSchema,
  config: DBJsonObjectSchema,
  configHash: DBSha256Schema,
  scope: DBJsonObjectSchema,
  scopeHash: DBSha256Schema,
  status: DBEnrichmentRunStatusSchema,
  statusDetail: DBEnrichmentRunStatusDetailSchema,
  isActive: z.boolean(),
  stats: DBJsonObjectSchema,
  startedAt: DBTimestampSchema.nullable(),
  finishedAt: DBTimestampSchema.nullable(),
  createdBy: CognitoUserIdSchema.nullable(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

/**
 * Client Schemas
 */

export const EnrichmentRunIdSchema = UUIDSchema;
export const EnrichmentRunEngineNameSchema = DBEnrichmentRunEngineNameSchema.openapi({
  description: 'The engine that produced the run',
  example: 'enrich-nile-question',
});
export const EnrichmentRunEngineVersionSchema = DBEnrichmentRunEngineVersionSchema;
export const EnrichmentRunModelSchema = DBEnrichmentRunModelSchema;
export const EnrichmentRunStatusSchema = DBEnrichmentRunStatusSchema.openapi({
  description: 'Processing status of the run',
  example: ProcessingStatus.Processed,
});
export const EnrichmentRunStatusDetailSchema = DBEnrichmentRunStatusDetailSchema;
export const EnrichmentRunConfigHashSchema = Sha256Schema.openapi({
  description:
    'Digest of the configuration. Together with the scope digest, it identifies the processed run.',
  example: 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
});
export const EnrichmentRunScopeHashSchema = Sha256Schema;
export const EnrichmentRunScopeSchema = JsonObjectSchema.openapi({
  description:
    'The question and content the run covered, and an attempt number. Part of the run identity, so re-running over unchanged content needs a new attempt number.',
});

export const EnrichmentRunSchema = z.object({
  id: EnrichmentRunIdSchema,
  questionId: UUIDSchema,
  engineName: EnrichmentRunEngineNameSchema,
  engineVersion: EnrichmentRunEngineVersionSchema,
  model: EnrichmentRunModelSchema,
  config: JsonObjectSchema,
  configHash: EnrichmentRunConfigHashSchema,
  scope: EnrichmentRunScopeSchema,
  scopeHash: EnrichmentRunScopeHashSchema,
  status: EnrichmentRunStatusSchema,
  statusDetail: EnrichmentRunStatusDetailSchema,
  isActive: z.boolean(),
  stats: JsonObjectSchema,
  startedAt: UnixTimestampSchema.nullable(),
  finishedAt: UnixTimestampSchema.nullable(),
  createdBy: CognitoUserIdSchema.nullable(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});
